import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// ── Benchmark journey backdrop ─────────────────────────────────────────────
// A stylised Singapore at night: Marina Bay landmarks (MBS, the Flyer, the
// Esplanade, the Supertrees), a living sea, the MRT running through the
// heartlands, and a data "digital twin" on top — one tower per resale
// project, clustered on its postal district and as tall as its avg PSF.
// Towers that still match the journey's answers glow; the rest sink.
//
// Scroll drives everything: progress (one unit per section) flies the camera
// between per-question shots, scroll speed spins the Flyer, whips the sea and
// the trains, and sweeps a scan line across the island; every section change
// fires a "scene cut" (shockwave + bloom punch + shake); the results bring
// the dawn.

export interface CityTower {
  projectName: string;
  district: string | null;
  avgPsf: number | null;
  txnCount: number;
}

export interface CitySceneOptions {
  mode: 'dark' | 'light';
  reducedMotion: boolean;
  compact: boolean;
}

// Approximate centroids (lat, lng) of Singapore's 28 postal districts.
const DISTRICT_LATLNG: Record<number, [number, number]> = {
  1: [1.2800, 103.8510], 2: [1.2760, 103.8430], 3: [1.2900, 103.8080], 4: [1.2650, 103.8200],
  5: [1.3000, 103.7700], 6: [1.2930, 103.8510], 7: [1.3000, 103.8600], 8: [1.3100, 103.8550],
  9: [1.3030, 103.8320], 10: [1.3180, 103.8100], 11: [1.3250, 103.8400], 12: [1.3300, 103.8550],
  13: [1.3350, 103.8750], 14: [1.3200, 103.8900], 15: [1.3050, 103.9050], 16: [1.3250, 103.9400],
  17: [1.3600, 103.9700], 18: [1.3550, 103.9450], 19: [1.3700, 103.8900], 20: [1.3600, 103.8450],
  21: [1.3400, 103.7750], 22: [1.3400, 103.7100], 23: [1.3700, 103.7600], 24: [1.4100, 103.7100],
  25: [1.4350, 103.7800], 26: [1.3900, 103.8200], 27: [1.4300, 103.8350], 28: [1.3950, 103.8700],
};
const MAP_SCALE = 800;
const toWorld = (lat: number, lng: number) => new THREE.Vector3((lng - 103.85) * MAP_SCALE, 0, -(lat - 1.35) * MAP_SCALE);
const CBD = toWorld(1.2800, 103.8510);
/** Marina Bay, pushed a little south-east of the CBD towers so it reads on its own. */
const BAY = CBD.clone().add(new THREE.Vector3(34, 0, 14));
/** East–West line, west to east, by district. */
const MRT_ROUTE = [22, 21, 5, 3, 1, 7, 14, 15, 16, 18, 17];
/** Six questions then the results: shots 0…6. */
const LAST_SECTION = 6;

const PALETTES = {
  dark: {
    bg: 0x041026, gridMajor: 0x123d73, gridMinor: 0x0a2a52,
    base: 0x1d4274, hot: 0xf47920, roof: 0xffd27a, pad: 0x5cc8ff,
    particle: 0x5cc8ff, arcA: 0x5cc8ff, arcB: 0xf9a64d,
    landmark: 0x3b6fb6, landmarkEdge: 0x7fd4ff, tree: 0x9b6bff, treeGlow: 0xc89bff,
    seaDeep: 0x03122c, seaCrest: 0x2d8fd6, seaGlow: 0xf9a64d,
    skyZenith: 0x020a1c, skyHorizon: 0x0f3a6e, dawn: 0xf47920, cloud: 0x5f8fcf,
    hotBoost: 1.7, additive: true, bloom: 0.95, sun: true, stars: true, vignette: 1.1, grain: 0.012,
  },
  light: {
    bg: 0xe8eef6, gridMajor: 0xb3c3d8, gridMinor: 0xd0dae7,
    base: 0x7f95b3, hot: 0xf47920, roof: 0xd35400, pad: 0x1a4f8a,
    particle: 0x1a4f8a, arcA: 0x1a4f8a, arcB: 0xf47920,
    landmark: 0x6f8db3, landmarkEdge: 0x1a4f8a, tree: 0x6a4bb8, treeGlow: 0x9b7bff,
    seaDeep: 0xb9d3ec, seaCrest: 0xffffff, seaGlow: 0xf47920,
    skyZenith: 0xcfe0f3, skyHorizon: 0xf7fbff, dawn: 0xffb270, cloud: 0xffffff,
    hotBoost: 1, additive: false, bloom: 0, sun: false, stars: false, vignette: 0.35, grain: 0.008,
  },
} as const;

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const smoother = (k: number) => k * k * k * (k * (k * 6 - 15) + 10);
const easeOutBack = (x: number) => { const s = 1.70158; return 1 + (s + 1) * Math.pow(x - 1, 3) + s * Math.pow(x - 1, 2); };
/** Frame-rate independent lerp factor for a per-60fps-frame rate. */
const damp = (rate: number, dt: number) => 1 - Math.pow(1 - rate, dt * 60);

const districtNumber = (value: string | null): number | null => {
  const n = Number(String(value ?? '').match(/\d+/)?.[0]);
  return DISTRICT_LATLNG[n] ? n : null;
};
const districtWorld = (d: number) => toWorld(DISTRICT_LATLNG[d][0], DISTRICT_LATLNG[d][1]);
const hashAngle = (text: string) => {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (h * 31 + text.charCodeAt(i)) | 0;
  return ((h >>> 0) % 3600) / 3600 * Math.PI * 2;
};
const radialTexture = (inner: string, mid: string, outer: string, size = 256) => {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grd.addColorStop(0, inner); grd.addColorStop(0.3, mid); grd.addColorStop(1, outer);
  g.fillStyle = grd; g.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

const FILM_SHADER = {
  uniforms: {
    tDiffuse: { value: null }, time: { value: 0 }, ab: { value: 0.0008 }, vig: { value: 1.1 }, grain: { value: 0.012 },
  },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader: `uniform sampler2D tDiffuse; uniform float time, ab, vig, grain; varying vec2 vUv;
    float rnd(vec2 p){ return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
    void main(){
      vec2 d = vUv - 0.5; float r2 = dot(d, d); vec2 o = d * ab * r2 * 40.0;
      vec4 base = texture2D(tDiffuse, vUv);
      vec3 c = vec3(texture2D(tDiffuse, vUv + o).r, base.g, texture2D(tDiffuse, vUv - o).b);
      c *= 1.0 - r2 * vig;
      c += (rnd(vUv * vec2(1920.0, 1080.0) + fract(time)) - 0.5) * grain;
      gl_FragColor = vec4(c, base.a);
    }`,
};

// Sea: moving crests, a warm reflection pooled under Marina Bay, fogged by distance.
const SEA_SHADER = {
  vertexShader: `uniform float time, amp; varying vec3 vWorld;
    void main(){
      vec4 w = modelMatrix * vec4(position, 1.0);
      w.y += (sin(w.x * 0.08 + time) * 0.35 + sin(w.z * 0.11 - time * 1.3) * 0.25) * amp;
      vWorld = w.xyz;
      gl_Position = projectionMatrix * viewMatrix * w;
    }`,
  fragmentShader: `uniform float time, amp, fogDensity; uniform vec3 deep, crest, glow, fogColor; uniform vec2 bay; varying vec3 vWorld;
    void main(){
      float w = sin(vWorld.x * 0.6 + time * 1.2 + sin(vWorld.z * 0.33 + time * 0.3) * 2.0) * sin(vWorld.z * 0.47 - time * 0.8);
      float line = smoothstep(0.86 - amp * 0.05, 0.995, w) * 0.55;
      vec3 col = mix(deep, crest, line);
      float d = length(vWorld.xz - bay);
      col += glow * exp(-d * 0.028) * (0.28 + 0.9 * line);
      float dist = length(cameraPosition - vWorld);
      float f = 1.0 - exp(-fogDensity * fogDensity * dist * dist);
      gl_FragColor = vec4(mix(col, fogColor, clamp(f, 0.0, 1.0)), 1.0);
    }`,
};

// Sky dome: zenith→horizon gradient, a dawn band that rises with the results, twinkling stars.
const SKY_SHADER = {
  vertexShader: 'varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
  fragmentShader: `uniform vec3 zenith, horizon, dawnColor; uniform float dawn, time, stars; varying vec3 vDir;
    float hash(vec3 p){ return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453); }
    void main(){
      float h = vDir.y;
      vec3 col = mix(horizon, zenith, smoothstep(-0.05, 0.55, h));
      col = mix(col, dawnColor, dawn * exp(-abs(h - 0.04) * 7.0) * 0.9);
      if (stars > 0.5 && h > 0.02) {
        vec3 cell = floor(vDir * 140.0);
        float s = hash(cell);
        float tw = 0.6 + 0.4 * sin(time * 1.5 + s * 40.0);
        col += vec3(0.8, 0.9, 1.0) * step(0.993, s) * tw * smoothstep(0.02, 0.2, h) * (1.0 - dawn * 0.8);
      }
      gl_FragColor = vec4(col, 1.0);
    }`,
};

interface Shot { p: THREE.Vector3; l: THREE.Vector3; fov: number; roll: number }

export class CityScene {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.5, 4000);
  private readonly clock = new THREE.Clock();
  private readonly palette: typeof PALETTES[keyof typeof PALETTES];
  private readonly opts: CitySceneOptions;
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private film: ShaderPass | null = null;
  private frameId = 0;
  private disposed = false;

  // towers
  private readonly towerGeo: THREE.BoxGeometry;
  private readonly towerMat: THREE.MeshLambertMaterial;
  private readonly roofGeo: THREE.PlaneGeometry;
  private readonly roofMat: THREE.MeshBasicMaterial;
  private towers: THREE.InstancedMesh | null = null;
  private roofs: THREE.InstancedMesh | null = null;
  private names: string[] = [];
  private pos: THREE.Vector3[] = [];
  private height = new Float32Array(0);
  private width = new Float32Array(0);
  private riseAt = new Float32Array(0);
  private morph = new Float32Array(0);       // 0 = filtered out, 1 = matching
  private morphTarget = new Float32Array(0);
  private morphAt = new Float32Array(0);     // wave delay per tower
  private settled = false;
  private matchNames: Set<string> | null = null;
  private hotMix = 1;

  // landscape
  private readonly sea: THREE.Mesh;
  private readonly sky: THREE.Mesh;
  private readonly flyer = new THREE.Group();
  private readonly treeGlows: THREE.Sprite[] = [];
  private readonly clouds: THREE.Sprite[] = [];
  private readonly trains: Array<{ mesh: THREE.Mesh; u: number; speed: number }> = [];
  private mrtCurve: THREE.CatmullRomCurve3 | null = null;
  private readonly scan: THREE.Group;

  // set pieces
  private readonly pads = new THREE.Group();
  private readonly arcs = new THREE.Group();
  private arcPulses: Array<{ curve: THREE.QuadraticBezierCurve3; mesh: THREE.Mesh; u: number; speed: number }> = [];
  private readonly marker = new THREE.Group();
  private readonly pin: THREE.Mesh;
  private readonly halo: THREE.Mesh;
  private readonly scans: THREE.Mesh[] = [];
  private readonly shock: THREE.Mesh;
  private shockT = -10;
  private readonly particles: THREE.Points;
  private readonly sunGlow: THREE.Sprite | null = null;

  // camera / scroll
  private readonly focus = new THREE.Vector3();
  private readonly focusTarget = new THREE.Vector3();
  private progressTarget = 0;
  private progress = 0;
  private lastProgressTarget = 0;
  private vel = 0;
  private readonly cam = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  private shake = 0;
  private kick = 0;
  private lastT = 0;
  private frame = 0;

  constructor(canvas: HTMLCanvasElement, opts: CitySceneOptions) {
    this.opts = opts;
    this.palette = PALETTES[opts.mode];
    const P = this.palette;
    const blend = P.additive ? THREE.AdditiveBlending : THREE.NormalBlending;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: !opts.compact, powerPreference: 'high-performance', alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, opts.compact ? 1.5 : 2));
    this.renderer.setClearColor(P.bg, 1);

    this.scene.fog = new THREE.FogExp2(P.bg, opts.mode === 'dark' ? 0.0024 : 0.0018);
    this.scene.add(new THREE.HemisphereLight(0xffffff, P.bg, opts.mode === 'dark' ? 1.5 : 2.2));
    const sun = new THREE.DirectionalLight(0xffffff, opts.mode === 'dark' ? 1.1 : 1.4);
    sun.position.set(70, 140, 50);
    this.scene.add(sun);

    // ── sky, sea, island ──
    this.sky = new THREE.Mesh(
      new THREE.SphereGeometry(1800, 32, 16),
      new THREE.ShaderMaterial({
        ...SKY_SHADER, side: THREE.BackSide, depthWrite: false, fog: false,
        uniforms: {
          zenith: { value: new THREE.Color(P.skyZenith) }, horizon: { value: new THREE.Color(P.skyHorizon) },
          dawnColor: { value: new THREE.Color(P.dawn) }, dawn: { value: 0 }, time: { value: 0 }, stars: { value: P.stars ? 1 : 0 },
        },
      }),
    );
    this.scene.add(this.sky);

    this.sea = new THREE.Mesh(
      new THREE.PlaneGeometry(2600, 2600, opts.compact ? 80 : 140, opts.compact ? 80 : 140),
      new THREE.ShaderMaterial({
        ...SEA_SHADER,
        uniforms: {
          time: { value: 0 }, amp: { value: 1 }, fogDensity: { value: 0.0024 },
          deep: { value: new THREE.Color(P.seaDeep) }, crest: { value: new THREE.Color(P.seaCrest) },
          glow: { value: new THREE.Color(P.seaGlow).multiplyScalar(opts.mode === 'dark' ? 0.9 : 0.35) },
          fogColor: { value: new THREE.Color(P.bg) }, bay: { value: new THREE.Vector2(BAY.x, BAY.z) },
        },
      }),
    );
    this.sea.rotation.x = -Math.PI / 2;
    this.sea.position.set(0, -1.8, 0);
    this.scene.add(this.sea);

    // The island: a rough Singapore outline carrying its own grid, so the
    // grid stops at the coast and the water is water.
    const gridCanvas = document.createElement('canvas');
    gridCanvas.width = gridCanvas.height = 64;
    const gg = gridCanvas.getContext('2d')!;
    gg.fillStyle = '#' + new THREE.Color(P.bg).getHexString(); gg.fillRect(0, 0, 64, 64);
    gg.strokeStyle = '#' + new THREE.Color(P.gridMinor).getHexString(); gg.lineWidth = 1;
    gg.strokeRect(0.5, 0.5, 63, 63);
    gg.strokeStyle = '#' + new THREE.Color(P.gridMajor).getHexString();
    gg.beginPath(); gg.moveTo(0, 0.5); gg.lineTo(64, 0.5); gg.moveTo(0.5, 0); gg.lineTo(0.5, 64); gg.stroke();
    const gridTex = new THREE.CanvasTexture(gridCanvas);
    gridTex.colorSpace = THREE.SRGBColorSpace;
    gridTex.wrapS = gridTex.wrapT = THREE.RepeatWrapping;
    gridTex.repeat.set(0.2, 0.2);
    const outline = new THREE.Shape();
    outline.moveTo(-200, 50);
    outline.splineThru([
      [-170, -20], [-120, -80], [-50, -100], [40, -96], [110, -80], [160, -50], [175, 0], [150, 40], [110, 62],
      [75, 78], [35, 86], [0, 80], [-40, 72], [-90, 70], [-150, 72], [-200, 50],
    ].map(([x, z]) => new THREE.Vector2(x, -z)));
    const island = new THREE.Mesh(new THREE.ShapeGeometry(outline, 24), new THREE.MeshBasicMaterial({ map: gridTex }));
    island.rotation.x = -Math.PI / 2;
    island.position.y = -0.05;
    this.scene.add(island);
    const shore = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(outline.getPoints(24).map((v) => new THREE.Vector3(v.x, 0.1, -v.y))),
      new THREE.LineBasicMaterial({ color: P.landmarkEdge, transparent: true, opacity: 0.45 }),
    );
    this.scene.add(shore);

    // Tower skin: dark face, bright vertical edges + floor lines. Multiplied by
    // the per-instance colour, so matches read as lit orange wireframes.
    const skin = document.createElement('canvas');
    skin.width = 32; skin.height = 64;
    const g = skin.getContext('2d')!;
    g.fillStyle = '#5a5a5a'; g.fillRect(0, 0, 32, 64);
    g.fillStyle = '#ffffff';
    for (let y = 0; y < 64; y += 8) g.fillRect(0, y, 32, 1);
    g.fillRect(0, 0, 2, 64); g.fillRect(30, 0, 2, 64);
    const skinTex = new THREE.CanvasTexture(skin);
    skinTex.colorSpace = THREE.SRGBColorSpace;
    this.towerGeo = new THREE.BoxGeometry(1, 1, 1);
    this.towerGeo.translate(0, 0.5, 0);
    this.towerMat = new THREE.MeshLambertMaterial({ map: skinTex });

    this.roofGeo = new THREE.PlaneGeometry(1, 1);
    this.roofGeo.rotateX(-Math.PI / 2);
    this.roofMat = new THREE.MeshBasicMaterial({ color: P.roof, transparent: true, opacity: 0.9, depthWrite: false, blending: blend });

    this.scene.add(this.pads, this.arcs);
    this.buildLandmarks(skinTex);
    this.buildMrt();

    // Scan line: a glowing bar + faint wall that sweeps the island as you scroll.
    this.scan = new THREE.Group();
    const bar = new THREE.Mesh(
      new THREE.PlaneGeometry(700, 2.2),
      new THREE.MeshBasicMaterial({ color: P.pad, transparent: true, opacity: 0.35, blending: blend, depthWrite: false, side: THREE.DoubleSide }),
    );
    bar.rotation.x = -Math.PI / 2; bar.position.y = 0.4;
    const wall = new THREE.Mesh(
      new THREE.PlaneGeometry(700, 60),
      new THREE.MeshBasicMaterial({ color: P.pad, transparent: true, opacity: 0.05, blending: blend, depthWrite: false, side: THREE.DoubleSide }),
    );
    wall.position.y = 30;
    this.scan.add(bar, wall);
    this.scene.add(this.scan);

    // Focus marker: hovering pin, beam, halo and scan rings over the centroid
    // of whatever still matches.
    const gold = new THREE.Color(P.arcB);
    this.pin = new THREE.Mesh(new THREE.OctahedronGeometry(2.6), new THREE.MeshBasicMaterial({ color: gold, wireframe: true }));
    this.pin.position.y = 52;
    const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 50, 8), new THREE.MeshBasicMaterial({ color: gold, transparent: true, opacity: 0.55 }));
    beam.position.y = 25;
    this.halo = new THREE.Mesh(new THREE.TorusGeometry(8, 0.14, 8, 96), new THREE.MeshBasicMaterial({ color: gold }));
    this.halo.rotation.x = Math.PI / 2;
    this.halo.position.y = 34;
    this.marker.add(this.pin, beam, this.halo);
    for (let i = 0; i < 3; i += 1) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.96, 1, 96),
        new THREE.MeshBasicMaterial({ color: P.pad, transparent: true, side: THREE.DoubleSide, depthWrite: false, blending: blend }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.25;
      this.scans.push(ring);
      this.marker.add(ring);
    }
    this.scene.add(this.marker);

    this.shock = new THREE.Mesh(
      new THREE.RingGeometry(0.92, 1, 160),
      new THREE.MeshBasicMaterial({ color: P.arcB, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false, blending: blend }),
    );
    this.shock.rotation.x = -Math.PI / 2;
    this.shock.position.y = 0.6;
    this.scene.add(this.shock);

    // Rising data particles.
    const count = opts.compact ? 600 : 1300;
    const pts = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      pts[i * 3] = (Math.random() - 0.5) * 320;
      pts[i * 3 + 1] = Math.random() * 100;
      pts[i * 3 + 2] = (Math.random() - 0.5) * 240;
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
    this.particles = new THREE.Points(pGeo, new THREE.PointsMaterial({
      color: P.particle, size: opts.mode === 'dark' ? 0.6 : 0.5, transparent: true,
      opacity: opts.mode === 'dark' ? 0.6 : 0.35, depthWrite: false, blending: blend,
    }));
    this.scene.add(this.particles);

    // Clouds drifting over the island.
    const cloudTex = radialTexture('rgba(255,255,255,.55)', 'rgba(255,255,255,.18)', 'rgba(255,255,255,0)');
    for (let i = 0; i < (opts.compact ? 5 : 9); i += 1) {
      const cloud = new THREE.Sprite(new THREE.SpriteMaterial({
        map: cloudTex, color: P.cloud, transparent: true, opacity: opts.mode === 'dark' ? 0.16 : 0.5, blending: blend, depthWrite: false,
      }));
      cloud.position.set((Math.random() - 0.5) * 700, 95 + Math.random() * 60, -260 + Math.random() * 420);
      cloud.scale.set(140 + Math.random() * 140, 50 + Math.random() * 40, 1);
      cloud.userData.speed = 1.5 + Math.random() * 2.5;
      this.clouds.push(cloud);
      this.scene.add(cloud);
    }

    // Dawn: a gold sun rises behind the skyline as the results arrive.
    if (P.sun) {
      this.sunGlow = new THREE.Sprite(new THREE.SpriteMaterial({
        map: radialTexture('rgba(255,226,160,.95)', 'rgba(249,166,77,.45)', 'rgba(244,121,32,0)'),
        fog: false, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false,
      }));
      this.sunGlow.scale.set(620, 620, 1);
      this.sunGlow.position.set(BAY.x + 160, -140, BAY.z + 900);
      this.scene.add(this.sunGlow);
    }

    // Post: bloom (dark only) + film pass (chromatic aberration, vignette, grain).
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    if (P.bloom > 0) {
      this.bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), opts.compact ? P.bloom * 0.75 : P.bloom, 0.5, 0.3);
      this.composer.addPass(this.bloom);
    }
    this.film = new ShaderPass(FILM_SHADER);
    this.film.uniforms.vig.value = P.vignette;
    this.film.uniforms.grain.value = opts.reducedMotion ? 0 : P.grain;
    this.composer.addPass(this.film);
    this.composer.addPass(new OutputPass());

    this.focus.copy(CBD); this.focusTarget.copy(CBD);
    const first = this.shot(0, 0);
    this.camera.position.copy(first.p);
    this.cam.copy(first.p);
    this.look.copy(first.l);
    this.frameId = requestAnimationFrame(this.loop);
  }

  // ── Marina Bay ──────────────────────────────────────────────────────────
  private buildLandmarks(skin: THREE.Texture) {
    const P = this.palette;
    const blend = P.additive ? THREE.AdditiveBlending : THREE.NormalBlending;
    const faceMat = new THREE.MeshLambertMaterial({ map: skin, color: P.landmark });
    const edgeMat = new THREE.LineBasicMaterial({ color: P.landmarkEdge, transparent: true, opacity: 0.7 });
    const goldMat = new THREE.LineBasicMaterial({ color: P.arcB, transparent: true, opacity: 0.9 });
    const outlined = (geo: THREE.BufferGeometry, mat: THREE.Material, lines: THREE.LineBasicMaterial) => {
      const group = new THREE.Group();
      group.add(new THREE.Mesh(geo, mat), new THREE.LineSegments(new THREE.EdgesGeometry(geo), lines));
      return group;
    };
    const bay = new THREE.Group();
    bay.position.copy(BAY);

    // Marina Bay Sands: three leaning towers under the SkyPark.
    const mbs = new THREE.Group();
    [-10, 0, 10].forEach((x, i) => {
      const geo = new THREE.BoxGeometry(6.5, 56, 4.5);
      geo.translate(0, 28, 0);
      const tower = outlined(geo, faceMat, edgeMat);
      tower.position.x = x;
      tower.rotation.z = (i - 1) * 0.045;
      mbs.add(tower);
    });
    const skypark = outlined(new THREE.BoxGeometry(40, 2.4, 8), faceMat, goldMat);
    skypark.position.set(0, 57.5, 0);
    skypark.rotation.y = 0.04;
    mbs.add(skypark);
    mbs.position.set(0, 0, 6);
    bay.add(mbs);

    // Singapore Flyer: a spoked wheel on two leaning legs, turning with the scroll.
    const wheel = new THREE.Group();
    const R = 17;
    wheel.add(new THREE.Mesh(new THREE.TorusGeometry(R, 0.35, 8, 64), new THREE.MeshBasicMaterial({ color: P.landmarkEdge })));
    wheel.add(new THREE.Mesh(new THREE.TorusGeometry(R - 1.6, 0.12, 6, 64), new THREE.MeshBasicMaterial({ color: P.landmarkEdge, transparent: true, opacity: 0.5 })));
    const spokes: number[] = [];
    const capsuleGeo = new THREE.SphereGeometry(0.8, 8, 8);
    const capsuleMat = new THREE.MeshBasicMaterial({ color: P.arcB });
    for (let i = 0; i < 28; i += 1) {
      const a = (i / 28) * Math.PI * 2;
      spokes.push(0, 0, 0, Math.cos(a) * R, Math.sin(a) * R, 0);
      const capsule = new THREE.Mesh(capsuleGeo, capsuleMat);
      capsule.position.set(Math.cos(a) * R, Math.sin(a) * R, 0);
      wheel.add(capsule);
    }
    const spokeGeo = new THREE.BufferGeometry();
    spokeGeo.setAttribute('position', new THREE.Float32BufferAttribute(spokes, 3));
    wheel.add(new THREE.LineSegments(spokeGeo, edgeMat));
    this.flyer.add(wheel);
    this.flyer.userData.wheel = wheel;
    const legMat = new THREE.MeshBasicMaterial({ color: P.landmarkEdge, transparent: true, opacity: 0.6 });
    [-1, 1].forEach((s) => {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.5, R + 4, 6), legMat);
      leg.position.set(0, (R + 1) / 2, s * 4);
      leg.rotation.x = s * 0.2;
      this.flyer.add(leg);
    });
    this.flyer.position.set(30, R + 1, -6);
    bay.add(this.flyer);

    // Esplanade: two spiked domes.
    const domeMat = new THREE.MeshBasicMaterial({ color: P.landmarkEdge, wireframe: true, transparent: true, opacity: 0.55 });
    [-6, 6].forEach((x) => {
      const dome = new THREE.Mesh(new THREE.SphereGeometry(6, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), domeMat);
      dome.position.set(-22 + x, 0, -8);
      bay.add(dome);
    });

    // Gardens by the Bay: Supertrees with glowing canopies.
    const trunkMat = new THREE.MeshBasicMaterial({ color: P.tree, transparent: true, opacity: 0.75 });
    const canopyMat = new THREE.LineBasicMaterial({ color: P.treeGlow, transparent: true, opacity: 0.85 });
    const glowTex = radialTexture('rgba(255,255,255,.9)', 'rgba(255,255,255,.3)', 'rgba(255,255,255,0)', 128);
    [[0, 22], [8, 26], [-7, 27], [4, 33], [-3, 36], [12, 34], [-12, 32]].forEach(([x, z], i) => {
      const h = 14 + ((i * 7) % 12);
      const r = 3 + ((i * 3) % 3);
      const tree = new THREE.Group();
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 1.4, h, 8, 1, true), trunkMat);
      trunk.position.y = h / 2;
      tree.add(trunk);
      const canopyGeo = new THREE.ConeGeometry(r, 6, 10, 1, true);
      canopyGeo.rotateX(Math.PI);
      const canopy = new THREE.LineSegments(new THREE.EdgesGeometry(canopyGeo), canopyMat);
      canopy.position.y = h + 3;
      tree.add(canopy);
      const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.15, 6, 32), new THREE.MeshBasicMaterial({ color: P.treeGlow }));
      ring.rotation.x = Math.PI / 2; ring.position.y = h + 6;
      tree.add(ring);
      const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex, color: P.treeGlow, transparent: true, opacity: 0.5, blending: blend, depthWrite: false }));
      glow.position.y = h + 4; glow.scale.set(r * 3.6, r * 3.6, 1);
      tree.add(glow);
      this.treeGlows.push(glow);
      tree.position.set(x, 0, z);
      bay.add(tree);
    });

    // The promenade curving round the water.
    const promenade = new THREE.Mesh(
      new THREE.RingGeometry(38, 39, 96, 1, 0, Math.PI),
      new THREE.MeshBasicMaterial({ color: P.landmarkEdge, transparent: true, opacity: 0.35, side: THREE.DoubleSide }),
    );
    promenade.rotation.x = -Math.PI / 2; promenade.position.set(4, 0.2, 8);
    bay.add(promenade);

    this.scene.add(bay);
  }

  // ── MRT East–West line with trains ──────────────────────────────────────
  private buildMrt() {
    const P = this.palette;
    const blend = P.additive ? THREE.AdditiveBlending : THREE.NormalBlending;
    const points = MRT_ROUTE.map((d) => districtWorld(d).setY(0.8));
    this.mrtCurve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.4);
    this.scene.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(this.mrtCurve.getPoints(200)),
      new THREE.LineBasicMaterial({ color: P.landmarkEdge, transparent: true, opacity: 0.55, blending: blend }),
    ));
    const stationGeo = new THREE.RingGeometry(1.6, 2.2, 24);
    const stationMat = new THREE.MeshBasicMaterial({ color: P.landmarkEdge, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthWrite: false });
    points.forEach((p) => {
      const station = new THREE.Mesh(stationGeo, stationMat);
      station.rotation.x = -Math.PI / 2;
      station.position.copy(p).setY(0.3);
      this.scene.add(station);
    });
    const trainGeo = new THREE.BoxGeometry(1.2, 0.9, 7);
    for (let i = 0; i < 3; i += 1) {
      const mesh = new THREE.Mesh(trainGeo, new THREE.MeshBasicMaterial({ color: 0xffffff }));
      this.scene.add(mesh);
      this.trains.push({ mesh, u: i / 3, speed: 0.025 + i * 0.004 });
    }
  }

  resize(width: number, height: number) {
    if (this.disposed || width <= 0 || height <= 0) return;
    this.renderer.setSize(width, height, false);
    this.composer?.setSize(width, height);
    this.bloom?.resolution.set(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /** Continuous scroll position: 0 = first question … n = results. */
  setProgress(progress: number) {
    this.progressTarget = progress;
  }

  /** Section change: shockwave at the focus, bloom/aberration punch, shake. */
  cut() {
    this.shockT = this.clock.getElapsedTime();
    this.shock.position.set(this.focus.x, 0.6, this.focus.z);
    this.kick = 1.2;
    if (!this.opts.reducedMotion) this.shake = 1.2;
  }

  setProjects(projects: CityTower[]) {
    if (this.disposed) return;
    if (this.towers) { this.scene.remove(this.towers); this.towers.dispose(); }
    if (this.roofs) { this.scene.remove(this.roofs); this.roofs.dispose(); }
    this.pads.children.forEach((pad) => { (pad as THREE.Mesh).geometry.dispose(); ((pad as THREE.Mesh).material as THREE.Material).dispose(); });
    this.pads.clear();

    const n = projects.length;
    this.names = projects.map((project) => project.projectName);
    this.pos = new Array(n);
    this.height = new Float32Array(n);
    this.width = new Float32Array(n);
    this.riseAt = new Float32Array(n);
    this.morph = new Float32Array(n).fill(1);
    this.morphTarget = new Float32Array(n).fill(1);
    this.morphAt = new Float32Array(n);
    if (!n) { this.towers = null; this.roofs = null; return; }

    const psfs = projects.map((project) => project.avgPsf).filter((v): v is number => v != null && v > 0).sort((a, b) => a - b);
    const lo = psfs.length ? psfs[Math.floor(psfs.length * 0.03)] : 0;
    const hi = psfs.length ? psfs[Math.floor(psfs.length * 0.97)] : 1;
    const span = Math.max(1, hi - lo);

    // Phyllotaxis spiral per district: biggest (most traded) projects at the heart.
    const order = projects.map((project, index) => ({ project, index }))
      .sort((a, b) => b.project.txnCount - a.project.txnCount);
    const perDistrict = new Map<number, number>();
    const extent = new Map<number, number>();
    const now = this.clock.getElapsedTime();
    order.forEach(({ project, index }) => {
      const d = districtNumber(project.district);
      let at: THREE.Vector3;
      if (d != null) {
        const k = perDistrict.get(d) ?? 0;
        perDistrict.set(d, k + 1);
        const r = 2.3 * Math.sqrt(k);
        const a = k * 2.39996;
        at = districtWorld(d).add(new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r));
        extent.set(d, Math.max(extent.get(d) ?? 0, r));
      } else {
        const a = hashAngle(project.projectName);
        at = new THREE.Vector3(Math.cos(a) * 30, 0, Math.sin(a) * 30);
      }
      this.pos[index] = at;
      const psf = project.avgPsf;
      const norm = psf == null ? 0.15 : clamp01((psf - lo) / span);
      this.height[index] = 3 + Math.pow(norm, 1.25) * 36;
      this.width[index] = 1.1 + Math.min(1.1, Math.log10(project.txnCount + 1) * 0.45);
      // The city builds itself outward from the CBD.
      this.riseAt[index] = this.opts.reducedMotion ? -10 : now + 0.15 + at.distanceTo(CBD) * 0.006 + Math.random() * 0.25;
    });

    const padMat = { color: this.palette.pad, transparent: true, opacity: 0.18, side: THREE.DoubleSide, depthWrite: false };
    extent.forEach((r, d) => {
      const pad = new THREE.Mesh(new THREE.RingGeometry(r + 2.6, r + 3, 64), new THREE.MeshBasicMaterial(padMat));
      pad.rotation.x = -Math.PI / 2;
      pad.position.copy(districtWorld(d)).setY(0.15);
      pad.userData.district = d;
      this.pads.add(pad);
    });

    this.towers = new THREE.InstancedMesh(this.towerGeo, this.towerMat, n);
    this.roofs = new THREE.InstancedMesh(this.roofGeo, this.roofMat, n);
    this.towers.frustumCulled = false;
    this.roofs.frustumCulled = false;
    const base = new THREE.Color(this.palette.base);
    for (let i = 0; i < n; i += 1) this.towers.setColorAt(i, base);
    this.scene.add(this.towers, this.roofs);
    this.settled = false;
    if (this.matchNames) this.setMatches(this.matchNames);
  }

  /** Projects still in play after the journey's answers. */
  setMatches(names: Set<string>) {
    this.matchNames = names;
    const n = this.names.length;
    if (!n) return;
    let matched = 0;
    const centroid = new THREE.Vector3();
    const byDistrict = new Map<THREE.Vector3, number>();
    for (let i = 0; i < n; i += 1) {
      const on = names.has(this.names[i]);
      this.morphTarget[i] = on ? 1 : 0;
      if (on) { matched += 1; centroid.add(this.pos[i]); }
    }
    if (matched) centroid.multiplyScalar(1 / matched); else centroid.copy(CBD);
    this.focusTarget.copy(centroid);
    // A wide net glows softly; a sharp shortlist blazes.
    this.hotMix = Math.max(0.3, Math.min(1, 1.15 - matched / n));

    // Changes ripple outward from the new focus.
    const now = this.clock.getElapsedTime();
    for (let i = 0; i < n; i += 1) {
      this.morphAt[i] = this.opts.reducedMotion ? 0 : now + this.pos[i].distanceTo(centroid) * 0.005;
    }
    this.settled = false;

    // District pads light up while they hold matches.
    const live = new Map<number, number>();
    this.pads.children.forEach((pad) => { live.set(pad.userData.district, 0); });
    for (let i = 0; i < n; i += 1) {
      if (!this.morphTarget[i]) continue;
      let best: number | null = null; let bestD = Infinity;
      this.pads.children.forEach((pad) => {
        const d = pad.position.distanceToSquared(this.pos[i]);
        if (d < bestD) { bestD = d; best = pad.userData.district; }
      });
      if (best != null) live.set(best, (live.get(best) ?? 0) + 1);
    }
    this.pads.children.forEach((pad) => {
      const count = live.get(pad.userData.district) ?? 0;
      ((pad as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = count > 0 ? 0.5 : 0.1;
      if (count > 0) byDistrict.set(pad.position, count);
    });

    // Data arcs from the focus to the districts holding the most matches.
    this.arcs.children.forEach((child) => {
      const mesh = child as THREE.Line | THREE.Mesh;
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    });
    this.arcs.clear();
    this.arcPulses = [];
    if (!matched || matched === n) return;
    const top = Array.from(byDistrict.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
    top.forEach(([target], index) => {
      const a = centroid.clone().setY(0.5);
      const b = target.clone().setY(0.5);
      if (a.distanceTo(b) < 4) return;
      const mid = a.clone().add(b).multiplyScalar(0.5);
      mid.y = 24 + a.distanceTo(b) * 0.35;
      const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
      const color = index % 2 ? this.palette.arcB : this.palette.arcA;
      this.arcs.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(curve.getPoints(64)),
        new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.55 }),
      ));
      for (let k = 0; k < 2; k += 1) {
        const mesh = new THREE.Mesh(new THREE.SphereGeometry(0.7, 10, 10), new THREE.MeshBasicMaterial({ color: 0xffffff }));
        this.arcs.add(mesh);
        this.arcPulses.push({ curve, mesh, u: Math.random(), speed: 0.12 + Math.random() * 0.1 });
      }
    });
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.frameId);
    this.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
      (Array.isArray(material) ? material : material ? [material] : []).forEach((m) => {
        const map = (m as THREE.MeshBasicMaterial).map;
        if (map) map.dispose();
        m.dispose();
      });
    });
    this.towers?.dispose();
    this.roofs?.dispose();
    this.composer?.dispose();
    // No forceContextLoss(): under React.StrictMode the effect re-runs on the
    // same canvas, and a renderer built on a lost context draws nothing.
    this.renderer.dispose();
  }

  // ── camera shots, one per section ──────────────────────────────────────
  private shot(i: number, t: number): Shot {
    const F = this.focus;
    const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
    switch (i) {
      case 0: { // Tenure — establishing aerial orbit over Marina Bay
        const a = 0.6 + t * 0.03;
        return { p: BAY.clone().add(V(Math.sin(a) * 150, 78, Math.cos(a) * 150)), l: BAY.clone().add(V(-10, 14, -10)), fov: 46, roll: 0 };
      }
      case 1: // Market — on the promenade, looking up at MBS and the Flyer
        return { p: BAY.clone().add(V(-44 + Math.sin(t * 0.2) * 4, 6, 44)), l: BAY.clone().add(V(10, 30, -4)), fov: 66, roll: -0.05 };
      case 2: // Budget — long lens from the sea: the skyline reads as a PSF chart
        return { p: V(BAY.x + Math.sin(t * 0.05) * 20, 22, BAY.z + 290), l: V(CBD.x + 10, 16, CBD.z), fov: 30, roll: 0 };
      case 3: // Location — god's-eye map of the districts
        return { p: V(F.x * 0.3, 290, F.z * 0.3 + 8), l: V(F.x * 0.3, 0, F.z * 0.3), fov: 50, roll: Math.sin(t * 0.1) * 0.35 };
      case 4: { // Amenities — low orbit around the focus, MRT running past
        const a = t * 0.12;
        return { p: F.clone().add(V(Math.sin(a) * 78, 30, Math.cos(a) * 78)), l: F.clone().add(V(0, 10, 0)), fov: 50, roll: 0.04 };
      }
      case 5: { // Building — vertigo dolly-zoom on the focus
        const s = this.opts.reducedMotion ? 0.5 : 0.5 + 0.5 * Math.sin(t * 0.45);
        const fov = 28 + s * 44;
        const dist = 30 / Math.tan((fov * Math.PI) / 360);
        return { p: F.clone().add(V(dist * 0.55, 16, dist * 0.83)), l: F.clone().add(V(0, 16, 0)), fov, roll: 0 };
      }
      default: // Results — wide pull-back over the bay to the dawn
        return { p: BAY.clone().add(V(50, 150, 270)), l: V(CBD.x, 10, CBD.z - 110), fov: 46, roll: 0 };
    }
  }

  private readonly loop = () => {
    if (this.disposed) return;
    this.frameId = requestAnimationFrame(this.loop);
    if (document.hidden) return;
    this.frame += 1;
    // Behind the results table the scene is ambience: 30fps is plenty.
    if (this.progress > LAST_SECTION - 0.4 && this.frame % 2) return;
    const t = this.clock.getElapsedTime();
    const dt = Math.min(0.05, t - this.lastT);
    this.lastT = t;
    const reduce = this.opts.reducedMotion;

    // Scroll speed (sections per second, smoothed) drives the kinetic effects.
    const dp = Math.abs(this.progressTarget - this.lastProgressTarget);
    this.lastProgressTarget = this.progressTarget;
    this.vel += ((reduce ? 0 : dp / Math.max(dt, 1 / 120)) - this.vel) * damp(0.08, dt);
    const speed = Math.min(2.5, this.vel);

    this.focus.lerp(this.focusTarget, reduce ? 1 : damp(0.04, dt));
    this.progress += (this.progressTarget - this.progress) * (reduce ? 1 : damp(0.06, dt));
    const c = Math.max(0, Math.min(LAST_SECTION, this.progress));
    const i = Math.min(Math.floor(c), LAST_SECTION - 1);
    const e = smoother(c - i);
    const A = this.shot(i, t);
    const B = this.shot(i + 1, t);
    this.cam.lerpVectors(A.p, B.p, e);
    this.cam.y += Math.sin(e * Math.PI) * 28;               // crane swoop between shots
    this.lookTarget.lerpVectors(A.l, B.l, e);
    const fov = A.fov + (B.fov - A.fov) * e;
    const roll = A.roll + (B.roll - A.roll) * e;
    if (!reduce) {                                          // handheld drift + cut shake
      this.cam.x += Math.sin(t * 1.3) * 0.4 + Math.sin(t * 2.7) * 0.2 + (Math.random() - 0.5) * this.shake;
      this.cam.y += Math.sin(t * 1.7) * 0.3 + (Math.random() - 0.5) * this.shake;
    }
    this.camera.position.lerp(this.cam, reduce ? 1 : damp(0.12, dt));
    this.look.lerp(this.lookTarget, reduce ? 1 : damp(0.12, dt));
    this.camera.fov += (fov - this.camera.fov) * (reduce ? 1 : damp(0.12, dt));
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(this.look);
    // Questions alternate sides; yaw the shot so the scenery fills the empty half.
    const sideA = i % 2 === 0 ? 1 : -1;
    const sideB = (i + 1) % 2 === 0 ? 1 : -1;
    const yaw = i + 1 >= LAST_SECTION ? sideA * (1 - e) : sideA + (sideB - sideA) * e;
    this.camera.rotateY(yaw * 0.16);
    this.camera.rotateZ(roll);
    this.shake *= Math.pow(0.9, dt * 60);
    this.kick *= Math.pow(0.95, dt * 60);

    this.updateTowers(t, dt);

    // landscape
    const seaMat = this.sea.material as THREE.ShaderMaterial;
    seaMat.uniforms.time.value = reduce ? 0 : t;
    seaMat.uniforms.amp.value = 1 + speed * 1.6;
    const skyMat = this.sky.material as THREE.ShaderMaterial;
    skyMat.uniforms.time.value = t;
    const dawn = clamp01(this.progress - (LAST_SECTION - 1));
    skyMat.uniforms.dawn.value = dawn;
    this.sky.position.copy(this.camera.position);
    const wheel = this.flyer.userData.wheel as THREE.Group;
    if (!reduce) wheel.rotation.z += dt * (0.08 + speed * 1.4);
    this.treeGlows.forEach((glow, n) => {
      (glow.material as THREE.SpriteMaterial).opacity = 0.35 + 0.25 * Math.sin(t * 1.4 + n) + this.kick * 0.5 + speed * 0.25;
    });
    this.clouds.forEach((cloud) => {
      if (reduce) return;
      cloud.position.x += dt * cloud.userData.speed * (1 + speed * 3);
      if (cloud.position.x > 420) cloud.position.x = -420;
    });
    if (this.mrtCurve) {
      this.trains.forEach((train) => {
        train.u = (train.u + dt * train.speed * (1 + speed * 6)) % 1;
        const at = this.mrtCurve!.getPointAt(train.u);
        const ahead = this.mrtCurve!.getPointAt(Math.min(1, train.u + 0.01));
        train.mesh.position.copy(at).setY(1.3);
        train.mesh.lookAt(ahead.setY(1.3));
      });
    }
    // The scan line sweeps the island once per section as you scroll through it.
    const frac = c - Math.floor(c);
    this.scan.position.z = -260 + frac * 420;
    this.scan.children.forEach((child, n) => {
      ((child as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = (n ? 0.05 : 0.35) * Math.min(1, 0.25 + speed * 1.5);
    });

    // marker + scans
    this.marker.position.copy(this.focus);
    this.scans.forEach((ring, n) => {
      const p = (t * 0.35 + n / 3) % 1;
      ring.scale.setScalar(4 + p * 36);
      (ring.material as THREE.MeshBasicMaterial).opacity = (1 - p) * 0.7;
    });
    this.pin.rotation.y = t * 1.2;
    this.pin.position.y = 52 + Math.sin(t * 2) * 1.2;
    this.halo.scale.setScalar(1 + Math.sin(t * 2) * 0.06);

    // shockwave
    const sk = (t - this.shockT) / 1.6;
    const shockMat = this.shock.material as THREE.MeshBasicMaterial;
    if (sk >= 0 && sk <= 1) {
      this.shock.scale.setScalar(4 + sk * 150);
      shockMat.opacity = (1 - sk) * 0.85;
    } else shockMat.opacity = 0;

    // arcs
    this.arcPulses.forEach((pulse) => {
      pulse.u = (pulse.u + dt * pulse.speed * (1 + this.kick * 3 + speed)) % 1;
      pulse.mesh.position.copy(pulse.curve.getPoint(pulse.u));
    });

    // particles drift upward, faster while scrolling
    if (!reduce) {
      const attr = this.particles.geometry.getAttribute('position') as THREE.BufferAttribute;
      const arr = attr.array as Float32Array;
      const lift = dt * (1 + speed * 4);
      for (let k = 1; k < arr.length; k += 3) {
        arr[k] += lift * (2 + (k % 7));
        if (arr[k] > 100) arr[k] = 0;
      }
      attr.needsUpdate = true;
    }

    // dawn for the results
    if (this.sunGlow) {
      (this.sunGlow.material as THREE.SpriteMaterial).opacity = dawn * 0.9;
      this.sunGlow.position.y = -140 + dawn * 190;
    }

    if (this.bloom) this.bloom.strength = (this.opts.compact ? this.palette.bloom * 0.75 : this.palette.bloom) + this.kick * 0.9 + speed * 0.25;
    if (this.film) {
      this.film.uniforms.time.value = t;
      this.film.uniforms.ab.value = 0.0008 + this.kick * 0.004 + speed * 0.0015;
    }
    if (this.composer) this.composer.render(dt);
    else this.renderer.render(this.scene, this.camera);
  };

  private updateTowers(t: number, dt: number) {
    const towers = this.towers;
    const roofs = this.roofs;
    if (!towers || !roofs || this.settled) return;
    const n = this.names.length;
    const base = new THREE.Color(this.palette.base);
    const hot = new THREE.Color(this.palette.hot).multiplyScalar(this.palette.hotBoost);
    hot.lerp(base, 1 - this.hotMix);
    const color = new THREE.Color();
    const m4 = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3();
    const p = new THREE.Vector3();
    const k = this.opts.reducedMotion ? 1 : damp(0.08, dt);
    let moving = false;
    for (let i = 0; i < n; i += 1) {
      const rise = this.opts.reducedMotion ? 1 : clamp01((t - this.riseAt[i]) / 1.1);
      if (rise < 1) moving = true;
      if (t >= this.morphAt[i]) {
        const delta = this.morphTarget[i] - this.morph[i];
        if (Math.abs(delta) > 0.002) { this.morph[i] += delta * k; moving = true; } else this.morph[i] = this.morphTarget[i];
      } else moving = true;
      const m = this.morph[i];
      const h = Math.max(0.05, this.height[i] * easeOutBack(rise) * (0.12 + 0.88 * m));
      const w = this.width[i];
      p.copy(this.pos[i]);
      s.set(w, h, w);
      towers.setMatrixAt(i, m4.compose(p, q, s));
      color.copy(base).lerp(hot, m);
      towers.setColorAt(i, color);
      p.y = h + 0.06;
      const roof = rise > 0.98 ? m * w * 0.9 : 0;
      s.set(Math.max(0.0001, roof), 1, Math.max(0.0001, roof));
      roofs.setMatrixAt(i, m4.compose(p, q, s));
    }
    towers.instanceMatrix.needsUpdate = true;
    if (towers.instanceColor) towers.instanceColor.needsUpdate = true;
    roofs.instanceMatrix.needsUpdate = true;
    this.settled = !moving;
  }
}
